import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Image } from '@connected/ui/components/image';
import { Separator } from '@connected/ui/components/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@connected/ui/components/table';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckmarkCircle } from '@/components/checkmark-circle';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { Observation } from '@/components/observation';
import {
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableHeader,
    ScrollableTableRow,
} from '@/components/scrollable-table';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_details/_installations/distribution-boards/$distributionId/',
)({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();

    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const distributionBoardsQuery = useQuery(portaalQueryOptions.electrical.distributionBoards.view({
        ElectricalProjectDistributionBoardId: params.distributionId,
    }));

    const observationQuery = useQuery(portaalQueryOptions.electrical.observations.list({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'DistributionBoard',
    }));

    return (
        <>
            <div className="flex flex-col sm:flex-row justify-between items-center">
                <div className="flex flex-col max-sm:flex-wrap gap-1 justify-start">
                    <h1 className="text-2xl font-semibold">
                        {distributionBoardsQuery?.data?.DistributionBoard?.Identifier}
                    </h1>
                    <h2 className="text-base font-medium text-text-primary/70">
                        {t('module-electrical.distribution-boards.floor')}
                        {' '}
                        {distributionBoardsQuery?.data?.DistributionBoard?.FloorNumber ?? 'Locatie onbekend'}
                        ,
                        {' '}
                        {t('module-electrical.distribution-boards.room')}
                        {' '}
                        {distributionBoardsQuery?.data?.DistributionBoard?.SpaceNumber ?? 'Locatie onbekend'}
                    </h2>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="link"
                        className="border px-3 rounded-lg h-10 w-fit text-foreground hover:no-underline hover:bg-background mt-2 md:mt-0"
                        onClick={() => setComingSoonModalOpen(true)}
                    >
                        <div className="flex items-center">
                            <p className="ml-2 mr-2 text-sm">{t('module-electrical.distribution-boards.distribution-board.buttons.notification')}</p>
                        </div>
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger className="flex items-center gap-2 border px-3 rounded-lg h-10 w-1/2 text-foreground hover:no-underline hover:bg-background">
                            <span className="text-sm">Verzoek</span>
                            <ChevronDown className="w-5 h-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="flex flex-col" align="end">
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Onderhoud
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Power quality
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Offerte vervanging
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Thermografie
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-row gap-4">
                <div className="w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        <p>
                            {distributionBoardsQuery?.data?.DistributionBoard?.Identifier}
                            {' '}
                            {t('module-electrical.distribution-boards.distribution-board.images.title')}
                        </p>
                    </h3>
                </div>
                {' '}
                <div className="w-3/4 rounded-2xl h-full">
                    {distributionBoardsQuery?.data?.DistributionBoard?.Files && distributionBoardsQuery?.data.DistributionBoard.Files.length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {distributionBoardsQuery?.data.DistributionBoard.Files.map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        previewer
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col md:flex-row md:gap-4">
                <div className="md: w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        {t('module-electrical.distribution-boards.distribution-board.notifications-table.title')}
                    </h3>
                </div>
                <div className="md:w-3/4 rounded-2xl border overflow-hidden">
                    <ScrollableTable maxHeight={19.9}>
                        <ScrollableTableHeader className="bg-background-secondary">
                            <ScrollableTableRow className="h-12">
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.name',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.status',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.comment',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.date',
                                    )}
                                </ScrollableTableHead>
                            </ScrollableTableRow>
                        </ScrollableTableHeader>
                        <ScrollableTableBody>
                            {observationQuery?.data?.Observations?.filter(obs => obs.ElectricalProjectDistributionBoardId === params.distributionId)?.length
                                ? observationQuery.data.Observations.filter(obs => obs.ElectricalProjectDistributionBoardId === params.distributionId).map(observation => (
                                        <ScrollableTableRow
                                            key={observation.ElectricalProjectObservationId}
                                            className="cursor-pointer hover:bg-background-secondary h-16"
                                        >
                                            <ScrollableTableCell>
                                                <div className="flex items-center gap-2">
                                                    <Observation
                                                        color={observation?.HexColorCode ?? '#FFFFFF'}
                                                        label={t(
                                                            'module-electrical.report.observations.details-table.columns.observation',
                                                        )}
                                                        className="scale-75"
                                                        children={[]}
                                                    />
                                                    <Link
                                                        to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                                                        params={{
                                                            locationId: params.locationId,
                                                            projectId: params.projectId,
                                                            distributionId: params.distributionId,
                                                            observationId: observation.ElectricalProjectObservationId!,
                                                        }}
                                                        className="font-bold text-primary cursor-pointer"
                                                    >
                                                        {t('module-electrical.report.observations.details-table.columns.observation')}
                                                    </Link>
                                                </div>
                                            </ScrollableTableCell>
                                            <ScrollableTableCell className="px-4">
                                                <CheckmarkCircle checked={observation.IsFixed} />
                                            </ScrollableTableCell>
                                            <ScrollableTableCell>
                                                {observation.Remark}
                                            </ScrollableTableCell>
                                            <ScrollableTableCell>
                                                {observation.Date ? new Date(observation.Date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                                            </ScrollableTableCell>
                                        </ScrollableTableRow>
                                    ))
                                : (
                                        <div className="flex items-center justify-center h-12 text-text-primary/50 w-full">
                                            <p className="w-full text-center">{t('module-electrical.pv-installations.messages.noObservationsFound')}</p>
                                        </div>
                                    )}
                        </ScrollableTableBody>
                    </ScrollableTable>
                </div>
            </div>
            <Separator className="my-5" />

            <div className="flex flex-col md:flex-row md:gap-4">
                <h3 className="text-base pb-4 md:w-1/4">
                    <p className="text-sm font-semibold text-text-secondary">{t('module-electrical.distribution-boards.distribution-board.measurements.overview.title')}</p>
                    <p className="text-sm text-text-tertiary">{t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.subtitle')}</p>
                </h3>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L1 (A)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.CurrentL1 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    L2 (A)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.CurrentL2 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L3 (A)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.CurrentL3 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    N (A)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.CurrentNeutral ?? '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-4">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4 mt-5">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.voltages-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L1 (L1-2) (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL1 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    L2 (L1-3) (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL2 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L3 (L2-3) (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL3 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    L1 N (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL1Neutral ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L2 - N (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL2Neutral ?? '-'}
                                </TableCell>
                                <TableCell>
                                    L3 - N (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL3Neutral ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-24 max-w-24">
                                    L1 - PE (V)
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL1Ground ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-24 max-w-24">
                                    L2 - PE (V)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL2Ground ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell>
                                    L3 - PE (V)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageL3Ground ?? '-'}
                                </TableCell>
                                <TableCell>
                                    N - PE (V)
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.VoltageNeutralGround ?? '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-4 mt-5">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.circuit-impedance-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.circuit-impedance-table.header.fase')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.circuit-impedance-table.header.impedance')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.circuit-impedance-table.header.current')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L1-PE
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceL1 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceCurrentL1 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L2-PE
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceL2 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceCurrentL2 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L3-PE
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceL3 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.GroundImpedanceCurrentL3 ?? '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex md:flex-row flex-col md:gap-4 mt-5">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.network-impedance-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.network-impedance-table.header.fase')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.network-impedance-table.header.impedance')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.network-impedance-table.header.current')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L1-N
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceL1 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceCurrentL1 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L2-N
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceL2 ?? '-'}
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceCurrentL2 ?? '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow
                                className="cursor-pointer hover:bg-background-secondary"
                            >
                                <TableCell className="min-w-32 max-w-32">
                                    L3-N
                                </TableCell>
                                <TableCell className="min-w-32 max-w-32">
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceL3 ?? '-'}
                                </TableCell>
                                <TableCell>
                                    {distributionBoardsQuery?.data?.DistributionBoard?.NeutralImpedanceCurrentL3 ?? '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-4 mt-5">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.header.type')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.header.value')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.header.trip-delay')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.header.test-button')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {distributionBoardsQuery?.data?.DistributionBoard?.ResidualCurrentDevices?.length
                                ? (
                                        distributionBoardsQuery?.data.DistributionBoard.ResidualCurrentDevices.map((device, idx) => (
                                            <TableRow
                                                key={device.Identifier ?? idx}
                                                className="cursor-pointer hover:bg-background-secondary"
                                            >
                                                <TableCell>
                                                    {device.Identifier ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.ResidualCurrentDeviceType ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.RatedCurrent ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.TripDelay ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.IsTestButtonFunctional ? t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.Approved') : t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.NotApproved')}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )
                                : (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center text-text-primary/50">
                                                {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.no-data')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Separator className="my-5" />

            <div className="flex flex-col md:flex-row md:gap-4">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.type')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.value')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.type2')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.value2')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.trip-delay')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.header.test-button')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {distributionBoardsQuery?.data?.DistributionBoard?.ResidualCurrentCircuitBreakerDevices?.length
                                ? (
                                        distributionBoardsQuery?.data.DistributionBoard.ResidualCurrentCircuitBreakerDevices.map((device, idx) => (
                                            <TableRow
                                                key={device.Identifier ?? idx}
                                                className="cursor-pointer hover:bg-background-secondary"
                                            >
                                                <TableCell>
                                                    {device.Identifier ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.ResidualCurrentDeviceType ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.RatedCurrent ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.TrippingCharacteristic ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.BreakerRatedCurrent ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.TripDelay ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.IsTestButtonFunctional ? t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.Approved') : t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-devices-table.NotApproved')}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )
                                : (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center text-text-primary/50">
                                                {t('module-electrical.distribution-boards.distribution-board.measurements.residual-current-breakers-table.no-data')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Separator className="my-5" />

            <div className="flex flex-col md:flex-row md:gap-4">
                <div className="md:w-1/4">

                </div>
                <div className="rounded-2xl border md:w-3/4">
                    <div className="py-2 bg-background-secondary">
                        <p className="pl-4 font-semibold tex-sm">{t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.title')}</p>
                    </div>
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.header.light-power')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.header.type')}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.header.value')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {distributionBoardsQuery?.data?.DistributionBoard?.FinalCircuitProtectiveDevices?.length
                                ? (
                                        distributionBoardsQuery?.data.DistributionBoard.FinalCircuitProtectiveDevices.map((device, idx) => (
                                            <TableRow
                                                key={device.Identifier ?? idx}
                                                className="cursor-pointer hover:bg-background-secondary"
                                            >
                                                <TableCell>
                                                    {device.Identifier ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.PowerPhaseKind === '3'
                                                        ? t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.power')
                                                        : t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.light')}
                                                </TableCell>
                                                <TableCell>
                                                    {device.TrippingCharacteristic ?? '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {device.BreakerRatedCurrent ?? '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )
                                : (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center text-text-primary/50">
                                                {t('module-electrical.distribution-boards.distribution-board.measurements.final-circuits-table.no-data')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Separator className="my-5" />
            <div className="flex flex-col md:flex-row gap-4 mb-10">
                <div className="md:w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        {t('module-electrical.distribution-boards.distribution-board.checklist-table.title')}
                    </h3>
                    <p className="text-sm text-text-primary/70">
                        {t('module-electrical.distribution-boards.distribution-board.checklist-table.subtitle')}
                    </p>
                </div>
                <div className="md:w-3/4 rounded-2xl border ">
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader className="bg-background-secondary">
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.checklist-table.header.part',
                                    )}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.checklist-table.header.status',
                                    )}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {distributionBoardsQuery?.data?.DistributionBoard?.CheckListItems?.map(item => (
                                <TableRow
                                    key={item.ElectricalProjectCheckListItemId}
                                    className="cursor-pointer hover:bg-background-secondary"
                                >
                                    <TableCell>
                                        {item.Description}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            item.ConclusionKind ? `module-electrical.distribution-boards.distribution-board.checklist-table.status.${item.ConclusionKind}` : '-',
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
